import { motion, AnimatePresence } from 'framer-motion'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { PRICING_PLANS, ROUTES } from '../config/constants'

/* ─── Desktop Tool features for comparison ─── */
const desktopFeatures = [
  { name: 'Load match video locally', free: true, pro: true },
  { name: 'Player Bag system', free: true, pro: true },
  { name: 'Timestamp marking & copy', free: true, pro: true },
  { name: 'Frame-accurate FFmpeg encoding', free: true, pro: true },
  { name: 'Batch export all players', free: true, pro: true },
  { name: 'Leftover clip compilation', free: true, pro: true },
  { name: 'Windows + macOS support', free: true, pro: true },
  { name: 'Unlimited exports', free: false, pro: true },
  { name: 'Watermark-free exports', free: false, pro: true },
  { name: 'Priority updates', free: false, pro: true },
]

/* ─── FAQ data ─── */
const faqs = [
  {
    q: 'How does hourly pricing work?',
    a: 'Our upload service pricing is based on the length of your raw match recording. A 2-hour match video at the Full Highlight plan would cost ₹1,999 × 2 = ₹3,998.',
  },
  {
    q: 'Is the desktop tool really free?',
    a: 'Yes! The core tool is completely free during beta. You can load videos, create player bags, mark timestamps, and export highlight reels at no cost.',
  },
  {
    q: 'What\'s the difference between Upload Service and Desktop Tool?',
    a: 'The Upload Service is hands-off — you upload and we deliver polished reels. The Desktop Tool gives you full control — you mark the moments and export yourself.',
  },
  {
    q: 'Can I get a refund?',
    a: 'If you\'re not satisfied with the upload service output, we\'ll revise it. Premium plan includes unlimited revisions.',
  },
]

function FAQItem({ faq, index }) {
  const [open, setOpen] = useState(false)
  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.08 }}
      className="border-b border-white/5 last:border-0"
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-5 text-left group"
      >
        <span className="text-sm font-semibold text-gray-200 group-hover:text-white transition-colors pr-4">
          {faq.q}
        </span>
        <motion.span
          animate={{ rotate: open ? 45 : 0 }}
          transition={{ duration: 0.2 }}
          className="text-pitch-green text-xl flex-shrink-0 font-light"
        >
          +
        </motion.span>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <p className="text-gray-500 text-sm pb-5 leading-relaxed">{faq.a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

export default function Pricing() {
  const [activeTab, setActiveTab] = useState('upload')

  return (
    <div className="pt-24 pb-20 px-4 min-h-screen relative overflow-hidden">
      {/* Background orbs */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 right-1/4 w-[400px] h-[400px] bg-pitch-green/8 rounded-full blur-[180px]" />
        <div className="absolute bottom-20 left-1/4 w-[300px] h-[300px] bg-gold/5 rounded-full blur-[150px]" />
      </div>

      <div className="container mx-auto relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10"
        >
          <span className="inline-block text-xs font-bold text-pitch-green uppercase tracking-[0.2em] mb-4">
            Pricing
          </span>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-black mb-4">
            <span className="text-white">Simple & </span>
            <span className="gradient-text">Transparent</span>
          </h1>
          <p className="text-lg text-gray-400 max-w-xl mx-auto">
            Two ways to create content. Pick what works for you.
          </p>
        </motion.div>

        {/* Tab Switcher */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="flex justify-center mb-14"
        >
          <div className="inline-flex rounded-2xl bg-white/[0.04] border border-white/[0.08] p-1.5 backdrop-blur-sm">
            {[
              { id: 'upload', label: 'Upload Service', icon: '📎', sub: 'We edit for you' },
              { id: 'tool', label: 'Desktop Tool', icon: '🖥️', sub: 'Do it yourself' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`relative px-6 md:px-8 py-3 rounded-xl text-sm font-bold transition-all duration-300 flex items-center gap-2 ${
                  activeTab === tab.id
                    ? 'text-stadium-black'
                    : 'text-gray-400 hover:text-gray-200'
                }`}
              >
                {activeTab === tab.id && (
                  <motion.div
                    layoutId="activeTab"
                    className="absolute inset-0 bg-pitch-green rounded-xl"
                    transition={{ type: 'spring', bounce: 0.15, duration: 0.5 }}
                  />
                )}
                <span className="relative z-10 text-base">{tab.icon}</span>
                <div className="relative z-10 text-left">
                  <span className="block leading-tight">{tab.label}</span>
                  <span className={`block text-[10px] font-normal ${
                    activeTab === tab.id ? 'text-stadium-black/60' : 'text-gray-600'
                  }`}>
                    {tab.sub}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </motion.div>

        {/* Content Area */}
        <AnimatePresence mode="wait">
          {activeTab === 'upload' ? (
            <motion.div
              key="upload"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.35 }}
            >
              {/* Upload Service Plans */}
              <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto mb-12">
                {PRICING_PLANS.map((plan, index) => (
                  <motion.div
                    key={plan.id}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className={`relative rounded-2xl p-7 border backdrop-blur-sm flex flex-col ${
                      plan.popular
                        ? 'border-pitch-green/40 bg-gradient-to-br from-pitch-green/[0.08] to-transparent scale-[1.03]'
                        : 'border-white/[0.08] bg-white/[0.02] hover:border-white/[0.15]'
                    } transition-all duration-300 hover:-translate-y-1`}
                  >
                    {plan.popular && (
                      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                        <div className="bg-pitch-green text-stadium-black px-4 py-1 rounded-full text-[10px] font-black uppercase tracking-wider shadow-lg shadow-pitch-green/30">
                          Most Popular
                        </div>
                      </div>
                    )}

                    <h3 className="text-xl font-bold mb-1 text-white">{plan.name}</h3>
                    <p className="text-gray-500 text-xs mb-5">{plan.description}</p>

                    <div className="mb-6">
                      <div className="flex items-baseline gap-1.5">
                        <span className="text-4xl font-black text-white">₹{plan.pricePerHour.toLocaleString('en-IN')}</span>
                        <span className="text-gray-500 text-sm">/hour</span>
                      </div>
                      <p className="text-gray-600 text-[11px] mt-1.5">
                        Example: 2-hour match = ₹{(plan.pricePerHour * 2).toLocaleString('en-IN')}
                      </p>
                    </div>

                    <ul className="space-y-3 mb-8 flex-1">
                      {plan.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2.5">
                          <div className="w-4 h-4 rounded-full bg-pitch-green/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <svg className="w-2.5 h-2.5 text-pitch-green" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <span className="text-gray-400 text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <Link
                      to={ROUTES.UPLOAD}
                      className={`block w-full text-center py-3.5 rounded-full font-bold text-sm transition-all hover:scale-[1.02] ${
                        plan.popular
                          ? 'bg-pitch-green text-stadium-black hover:bg-neon-green shadow-lg shadow-pitch-green/20'
                          : 'border border-white/15 text-white hover:border-pitch-green/40 hover:text-pitch-green'
                      }`}
                    >
                      Get Started
                    </Link>
                  </motion.div>
                ))}
              </div>

              {/* Hourly pricing explainer */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="max-w-3xl mx-auto"
              >
                <div className="rounded-2xl border border-pitch-green/15 bg-pitch-green/[0.04] p-6 backdrop-blur-sm">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-base">💡</span>
                    <h3 className="text-sm font-bold text-white">How Hourly Pricing Works</h3>
                  </div>
                  <p className="text-gray-500 text-xs mb-4 leading-relaxed">
                    Our pricing scales with your video duration. The per-hour rate multiplies by the length of your match recording.
                  </p>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { dur: '1 Hour', note: 'Base rate' },
                      { dur: '2 Hours', note: 'Price × 2' },
                      { dur: '3+ Hours', note: 'Price × hours' },
                    ].map((ex, i) => (
                      <div key={i} className="bg-stadium-black/40 rounded-xl p-3 text-center">
                        <p className="text-pitch-green font-bold text-xs mb-0.5">{ex.dur}</p>
                        <p className="text-gray-600 text-[10px]">{ex.note}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          ) : (
            <motion.div
              key="tool"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.35 }}
              className="max-w-4xl mx-auto"
            >
              {/* Desktop Tool Pricing */}
              <div className="grid md:grid-cols-2 gap-6 mb-12">
                {/* Free Tier */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="rounded-2xl p-7 border border-white/[0.08] bg-white/[0.02] backdrop-blur-sm flex flex-col hover:border-white/[0.15] transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-xl font-bold text-white">Free Beta</h3>
                    <div className="px-2 py-0.5 bg-pitch-green/20 rounded-full text-[9px] text-pitch-green font-bold">
                      CURRENT
                    </div>
                  </div>
                  <p className="text-gray-500 text-xs mb-5">Full access during beta period</p>

                  <div className="mb-6">
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-5xl font-black gradient-text">₹0</span>
                      <span className="text-gray-500 text-sm">/forever (beta)</span>
                    </div>
                    <p className="text-gray-600 text-[11px] mt-1.5">
                      No credit card required
                    </p>
                  </div>

                  <ul className="space-y-3 mb-8 flex-1">
                    {desktopFeatures.filter(f => f.free).map((f, i) => (
                      <li key={i} className="flex items-start gap-2.5">
                        <div className="w-4 h-4 rounded-full bg-pitch-green/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <svg className="w-2.5 h-2.5 text-pitch-green" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <span className="text-gray-400 text-sm">{f.name}</span>
                      </li>
                    ))}
                  </ul>

                  <Link
                    to={ROUTES.DOWNLOAD}
                    className="block w-full text-center py-3.5 rounded-full font-bold text-sm bg-pitch-green text-stadium-black hover:bg-neon-green transition-all hover:scale-[1.02] shadow-lg shadow-pitch-green/20"
                  >
                    Download Free
                  </Link>
                </motion.div>

                {/* Pro Tier */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="relative rounded-2xl p-7 border border-gold/30 bg-gradient-to-br from-gold/[0.06] to-transparent backdrop-blur-sm flex flex-col hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <div className="bg-gold text-stadium-black px-4 py-1 rounded-full text-[10px] font-black uppercase tracking-wider shadow-lg shadow-gold/30">
                      Coming Soon
                    </div>
                  </div>

                  <h3 className="text-xl font-bold mb-1 text-white">Pro</h3>
                  <p className="text-gray-500 text-xs mb-5">For serious content creators</p>

                  <div className="mb-6">
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-5xl font-black text-white">TBD</span>
                    </div>
                    <p className="text-gray-600 text-[11px] mt-1.5">
                      Pricing announced at launch
                    </p>
                  </div>

                  <ul className="space-y-3 mb-8 flex-1">
                    {desktopFeatures.map((f, i) => (
                      <li key={i} className="flex items-start gap-2.5">
                        <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                          f.pro ? 'bg-gold/20' : 'bg-white/5'
                        }`}>
                          {f.pro ? (
                            <svg className="w-2.5 h-2.5 text-gold" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          ) : (
                            <svg className="w-2.5 h-2.5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                        <span className={`text-sm ${f.pro && !f.free ? 'text-gold/80 font-medium' : 'text-gray-400'}`}>
                          {f.name}
                          {f.pro && !f.free && <span className="ml-1 text-[10px] text-gold/50">(Pro only)</span>}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <button
                    disabled
                    className="block w-full text-center py-3.5 rounded-full font-bold text-sm border border-gold/30 text-gold/50 cursor-not-allowed opacity-70"
                  >
                    Notify Me at Launch
                  </button>
                </motion.div>
              </div>

              {/* Feature Comparison Table */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="rounded-2xl border border-white/[0.08] bg-white/[0.02] backdrop-blur-sm overflow-hidden"
              >
                <div className="px-6 py-4 border-b border-white/5">
                  <h3 className="text-sm font-bold text-white">Feature Comparison</h3>
                </div>
                <div className="divide-y divide-white/5">
                  <div className="grid grid-cols-3 px-6 py-3 text-[10px] font-bold uppercase tracking-wider text-gray-500">
                    <span>Feature</span>
                    <span className="text-center">Free Beta</span>
                    <span className="text-center">Pro</span>
                  </div>
                  {desktopFeatures.map((f, i) => (
                    <div key={i} className="grid grid-cols-3 px-6 py-3 items-center hover:bg-white/[0.02] transition-colors">
                      <span className="text-sm text-gray-400">{f.name}</span>
                      <span className="text-center">
                        {f.free ? (
                          <span className="inline-flex w-5 h-5 rounded-full bg-pitch-green/20 items-center justify-center">
                            <svg className="w-3 h-3 text-pitch-green" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </span>
                        ) : (
                          <span className="text-gray-600">—</span>
                        )}
                      </span>
                      <span className="text-center">
                        <span className="inline-flex w-5 h-5 rounded-full bg-gold/20 items-center justify-center">
                          <svg className="w-3 h-3 text-gold" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </span>
                      </span>
                    </div>
                  ))}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* FAQ Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl mx-auto mt-24"
        >
          <div className="section-divider mb-16" />
          <h2 className="text-3xl md:text-4xl font-black text-center mb-10">
            <span className="text-white">Frequently </span>
            <span className="gradient-text">Asked</span>
          </h2>
          <div className="rounded-2xl border border-white/[0.08] bg-white/[0.02] backdrop-blur-sm px-6">
            {faqs.map((faq, i) => (
              <FAQItem key={i} faq={faq} index={i} />
            ))}
          </div>
        </motion.div>

        {/* Bottom note */}
        <p className="text-center text-gray-600 text-xs mt-10">
          All prices in Indian Rupees (₹). Delivery timeline starts after payment confirmation.
        </p>
      </div>
    </div>
  )
}
